import { getFirmTimeline, getLawyerTimeline } from '@shared/api/timeline';
import type {
  TimelineCounts,
  TimelineItem,
  TimelineQuery,
} from '@domain/timeline/model';
import { parseTimeline } from '@domain/timeline/parseTimeline';

type TimelineScope =
  | { kind: 'firm'; id: string }
  | { kind: 'lawyer'; id: string };
/// `cursor` は意図的に含めない — これはページ単位の一過性パラメータであり、
/// `timelineStore.query` (フィルタ再現/reload に使われる再利用可能な状態) へ
/// 永続させると、フィルタ変更を伴わない再取得 (例: 作成後の `refreshTimeline`)
/// が意図せず古いカーソルを引き継いでしまう。cursor は [`run`] の呼び出し
/// パラメータとしてのみ扱う。
type TimelineQueryState = Required<
  Pick<TimelineQuery, 'limit' | 'type' | 'contactLogType'>
>;

const DEFAULT_TIMELINE_LIMIT = 50;
const TIMELINE_LIMIT_INCREMENT = 30;
const MAX_TIMELINE_LIMIT = 500;

interface State {
  scope: TimelineScope | null;
  query: TimelineQueryState;
  items: TimelineItem[];
  counts: TimelineCounts | null;
  /// 直前の応答が返した継続トークン。`null` = 最終ページ (または未取得)。
  /// U5 (UI のページングボタン) はこれを読んでボタンの有効/無効を判定する想定 —
  /// このストア自身は cursor の配線 (API クライアント面) までを担う。
  nextCursor: string | null;
  loading: boolean;
  error: string | null;
}

const DEFAULT_QUERY: TimelineQueryState = {
  limit: DEFAULT_TIMELINE_LIMIT,
  type: 'all',
  contactLogType: 'all',
};

const state = $state<State>({
  scope: null,
  query: { ...DEFAULT_QUERY },
  items: [],
  counts: null,
  nextCursor: null,
  loading: false,
  error: null,
});
let token = 0;

function normalizeQuery(query: TimelineQuery = {}): TimelineQueryState {
  const requestedLimit = query.limit ?? DEFAULT_QUERY.limit;
  const safeLimit = Number.isFinite(requestedLimit)
    ? Math.floor(requestedLimit)
    : DEFAULT_QUERY.limit;
  return {
    limit: Math.min(Math.max(1, safeLimit), MAX_TIMELINE_LIMIT),
    type: query.type ?? DEFAULT_QUERY.type,
    contactLogType: query.contactLogType ?? DEFAULT_QUERY.contactLogType,
  };
}

async function run(
  scope: TimelineScope,
  query: TimelineQuery = {},
): Promise<void> {
  const t = ++token;
  const nextQuery = normalizeQuery(query);
  const cursor = query.cursor;
  // cursor 付きリクエスト = keyset の次ページ取得。既存 items へ追記する
  // (それ以外はフィルタ変更や再読込なので置き換える)。
  const append = cursor !== undefined;
  state.loading = true;
  state.error = null;
  state.scope = scope;
  state.query = nextQuery;
  try {
    const requestQuery = { ...nextQuery, cursor };
    const raw =
      scope.kind === 'firm'
        ? await getFirmTimeline(scope.id, requestQuery)
        : await getLawyerTimeline(scope.id, requestQuery);
    if (t !== token) return;
    const parsed = parseTimeline(raw);
    state.items = append ? [...state.items, ...parsed.items] : parsed.items;
    state.counts = parsed.counts;
    state.nextCursor = parsed.next_cursor;
  } catch (err) {
    if (t !== token) return;
    state.error = err instanceof Error ? err.message : String(err);
    state.items = [];
    state.counts = null;
    state.nextCursor = null;
  } finally {
    if (t === token) state.loading = false;
  }
}

export const timelineStore = {
  get items(): TimelineItem[] {
    return state.items;
  },
  get counts(): TimelineCounts | null {
    return state.counts;
  },
  get query(): TimelineQueryState {
    return state.query;
  },
  get loading(): boolean {
    return state.loading;
  },
  get error(): string | null {
    return state.error;
  },
  /// 直前の応答の keyset 継続トークン。`null` = 次ページなし。
  get nextCursor(): string | null {
    return state.nextCursor;
  },
  get canLoadMore(): boolean {
    return state.scope !== null && state.query.limit < MAX_TIMELINE_LIMIT;
  },
  async loadFirm(firmId: string, query: TimelineQuery = {}): Promise<void> {
    await run({ kind: 'firm', id: firmId }, query);
  },
  async loadLawyer(lawyerId: string, query: TimelineQuery = {}): Promise<void> {
    await run({ kind: 'lawyer', id: lawyerId }, query);
  },
  async loadMore(increment = TIMELINE_LIMIT_INCREMENT): Promise<void> {
    if (!state.scope) return;
    const nextLimit = Math.min(
      MAX_TIMELINE_LIMIT,
      state.query.limit + increment,
    );
    if (nextLimit === state.query.limit) return;
    await run(state.scope, { ...state.query, limit: nextLimit });
  },
  /**
   * keyset の次ページを取得し、既存 items へ追記する。UI のページングボタン
   * (もっと見る等) をどのタイミングで呼ぶかは U5 の領分 — このストアは
   * `nextCursor` の保持と cursor 付きリクエストの配線までを担う。
   */
  async loadNextPage(): Promise<void> {
    if (!state.scope || !state.nextCursor) return;
    await run(state.scope, { ...state.query, cursor: state.nextCursor });
  },
  /** Re-fetch the current scope (used after create / refresh). */
  async reload(query: TimelineQuery = state.query): Promise<void> {
    if (state.scope) await run(state.scope, query);
  },
  clear(): void {
    token++;
    state.scope = null;
    state.query = { ...DEFAULT_QUERY };
    state.items = [];
    state.counts = null;
    state.nextCursor = null;
    state.error = null;
    state.loading = false;
  },
};
